import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { colors } from "../../../theme/colors";

export interface CreateNewQRBatchFormData {
  quantity: string;
  cgdName: string;
  remarks: string;
}

interface CreateNewQRBatchModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateNewQRBatchFormData) => Promise<boolean>;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onClearError?: () => void;
}

const initialFormData: CreateNewQRBatchFormData = {
  quantity: "",
  cgdName: "Indraprastha Gas Limited (IGL)",
  remarks: "",
};

const cgdOptions = [
  {
    label: "Adani Total Gas Limited (ATGL)",
    value: "Adani Total Gas Limited (ATGL)",
  },
  {
    label: "Indraprastha Gas Limited (IGL)",
    value: "Indraprastha Gas Limited (IGL)",
  },
  // { label: "Gujarat Gas", value: "Gujarat Gas" },
  // { label: "Mahanagar Gas", value: "Mahanagar Gas" },
  // { label: "AG&P Pratham", value: "AG&P Pratham" },
];

const CreateNewQRBatchModal: React.FC<CreateNewQRBatchModalProps> = ({
  open,
  onClose,
  onSave,
  isSubmitting = false,
  errorMessage = null,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<CreateNewQRBatchFormData>(initialFormData);
  const [quantityError, setQuantityError] = useState("");
  const [cgdError, setCgdError] = useState("");
  const [isQuantityTouched, setIsQuantityTouched] = useState(false);
  const [isCgdTouched, setIsCgdTouched] = useState(false);

  const fieldLabelSx = {
    fontSize: "18px",
    fontWeight: 700,
    color: "#232323",
    mb: 1,
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#CFCFCF",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#A8A8A8",
        borderWidth: 1,
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "18px",
      color: "#333333",
    },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: 0.75,
    },
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setQuantityError("");
    setCgdError("");
    setIsQuantityTouched(false);
    setIsCgdTouched(false);
  };

  const validateQuantity = (value: string) => {
    if (!value.trim()) {
      return "Quantity is required";
    }

    const parsedValue = Number(value);
    if (
      !Number.isInteger(parsedValue) ||
      parsedValue < 1 ||
      parsedValue > 8000
    ) {
      return "Number of QR codes must be between 1 and 8000";
    }

    return "";
  };

  const validateCgd = (value: string) => {
    if (!value.trim()) {
      return "CGD is required";
    }

    return "";
  };

  const handleClose = () => {
    resetForm();
    onClearError?.();
    onClose();
  };

  const handleSave = async () => {
    const nextQuantityError = validateQuantity(formData.quantity);
    const nextCgdError = validateCgd(formData.cgdName);
    setQuantityError(nextQuantityError);
    setCgdError(nextCgdError);
    setIsQuantityTouched(true);
    setIsCgdTouched(true);

    if (nextQuantityError || nextCgdError) {
      return;
    }

    onClearError?.();
    const isSuccess = await onSave({
      quantity: formData.quantity,
      cgdName: formData.cgdName,
      remarks: formData.remarks.trim(),
    });

    if (!isSuccess) {
      return;
    }

    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "22px",
            maxWidth: 768,
            overflow: "hidden",
            boxShadow: "0 22px 70px rgba(0, 0, 0, 0.34)",
          },
        },
      }}
    >
      <Box
        sx={{
          px: 4,
          pt: 3.2,
          pb: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            color: colors.black,
            lineHeight: 1.15,
          }}
        >
          Create New QR Batch
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            width: 56,
            height: 56,
            bgcolor: "#F0F0F0",
            color: colors.black,
            "&:hover": {
              bgcolor: "#E8E8E8",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 34 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 4, pt: "18px !important", pb: 4.2 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box>
            <Typography sx={fieldLabelSx}>Quantity *</Typography>
            <TextField
              fullWidth
              value={formData.quantity}
              placeholder="Number of QR codes to generate (1-8000)"
              onChange={(event) => {
                const sanitizedValue = event.target.value
                  .replace(/[^0-9]/g, "")
                  .slice(0, 6);
                setFormData((prev) => ({ ...prev, quantity: sanitizedValue }));
                onClearError?.();

                if (isQuantityTouched) {
                  setQuantityError(validateQuantity(sanitizedValue));
                }
              }}
              onBlur={() => {
                setIsQuantityTouched(true);
                setQuantityError(validateQuantity(formData.quantity));
              }}
              error={Boolean(isQuantityTouched && quantityError)}
              helperText={
                isQuantityTouched && quantityError ? quantityError : " "
              }
              slotProps={{
                htmlInput: {
                  inputMode: "numeric",
                  maxLength: 6,
                },
              }}
              sx={{
                ...fieldSx,
                "& .MuiOutlinedInput-root": {
                  ...fieldSx["& .MuiOutlinedInput-root"],
                  minHeight: 62,
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>CGD *</Typography>
            <Select
              fullWidth
              value={formData.cgdName}
              onChange={(event: SelectChangeEvent<string>) => {
                setFormData((prev) => ({
                  ...prev,
                  cgdName: event.target.value,
                }));
                onClearError?.();

                if (isCgdTouched) {
                  setCgdError(validateCgd(event.target.value));
                }
              }}
              onBlur={() => {
                setIsCgdTouched(true);
                setCgdError(validateCgd(formData.cgdName));
              }}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              error={Boolean(isCgdTouched && cgdError)}
              sx={{
                ...fieldSx,
                "& .MuiOutlinedInput-root": {
                  ...fieldSx["& .MuiOutlinedInput-root"],
                  minHeight: 62,
                },
              }}
            >
              {cgdOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Typography
              sx={{
                minHeight: 20,
                mt: 0.75,
                fontSize: "0.75rem",
                color: "#D32F2F",
              }}
            >
              {isCgdTouched && cgdError ? cgdError : " "}
            </Typography>
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Remarks (Optional)</Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder="Add notes about this batch..."
              value={formData.remarks}
              onChange={(event) => {
                setFormData((prev) => ({
                  ...prev,
                  remarks: event.target.value,
                }));
                onClearError?.();
              }}
              sx={{
                ...fieldSx,
                "& .MuiOutlinedInput-root": {
                  ...fieldSx["& .MuiOutlinedInput-root"],
                  alignItems: "flex-start",
                  minHeight: 128,
                },
                "& .MuiInputBase-inputMultiline": {
                  fontSize: "18px",
                  lineHeight: 1.4,
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <Box
        sx={{
          px: 4,
          pb: 4,
          pt: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSubmitting}
          sx={{
            width: "100%",
            minHeight: 64,
            textTransform: "none",
            bgcolor: "#2E7D32",
            borderRadius: "6px",
            fontSize: "18px",
            fontWeight: 700,
            boxShadow: "0 4px 10px rgba(46, 125, 50, 0.28)",
            "&:hover": {
              bgcolor: "#27642A",
              boxShadow: "0 4px 10px rgba(46, 125, 50, 0.28)",
            },
          }}
        >
          {isSubmitting ? "Saving..." : "Create Batch"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateNewQRBatchModal;
